"use client";
import { useClarityAnalytics, useUmamiAnalytics } from "@analytics";
import { LocaleLink, useLocalePathname } from "@i18n/routing";
import { type Config, config } from "@repo/config";
import { creditPacks, plans as creditPlans } from "@repo/config/credits";
import { DEFAULT_MODEL_CONFIGS } from "@saas/ai-image-generator/types";
import { useLoginModal } from "@saas/auth/components/LoginModalProvider";
import { getI2VCreditCost } from "@saas/image-to-video/types";
import { usePlanData } from "@saas/payments/hooks/plan-data";
import { usePurchases } from "@saas/payments/hooks/purchases";
import {
	purchasesQueryKey,
	useChangeSubscriptionPlanMutation,
	useCreateCheckoutLinkMutation,
} from "@saas/payments/lib/api";
import { normalizePage } from "@saas/payments/lib/track-page";
import type { PlanId } from "@saas/payments/types";
import { useLocaleCurrency } from "@shared/hooks/locale-currency";
import { useRouter } from "@shared/hooks/router";
import { useAbVariant } from "@shared/hooks/use-ab-variant";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { Tabs, TabsList, TabsTrigger } from "@ui/components/tabs";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@ui/components/tooltip";
import { cn } from "@ui/lib";
import {
	ArrowRightIcon,
	BadgePercentIcon,
	BriefcaseIcon,
	CheckIcon,
	ChevronDownIcon,
	GiftIcon,
	HelpCircleIcon,
	ImageIcon,
	InfinityIcon,
	KeyboardIcon,
	type LucideIcon,
	MessageSquareTextIcon,
	PhoneIcon,
	StarIcon,
	XIcon,
	Zap,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { toast } from "sonner";
import { ChangePlanButton } from "./ChangePlanButton";
import { WechatPayLogo } from "./WechatPayLogo";

const plans = config.payments.plans as Config["payments"]["plans"];

/** 「N 积分 ≈ 多少条视频」：按最便宜的 Basic 480p 5 秒一条算（6 积分，2026-09-30
 *  起 Basic 也是生成页默认模型）。直接读模型定价，别手抄——之前抄成已下架模型的价，
 *  把额度说少了 10 倍；后来按 Fast 3.0 的 12 积分算，又把条数说少了一半 */
const CREDITS_PER_VIDEO = getI2VCreditCost("basic", "480p", 5);

/** 「N 积分 ≈ 多少张图」用的换算率。取模型下拉里最便宜的那档（gpt-image-2 的 1K
 *  价），之前这里按 1 积分 1 张算，把张数说多了一倍。注意 2026-09-17 起生成页
 *  的默认模型是 v3.0（6 积分），这里仍按最便宜档报张数，是营销口径，不是默认体验。 */
const CREDITS_PER_IMAGE = DEFAULT_MODEL_CONFIGS["gpt-image-2"].credits;

/** 「最多 N 条视频或 M 张图」+ 说明换算口径的 tooltip。订阅卡和积分包共用。 */
function AllowanceLine({ credits }: { credits: number }) {
	const t = useTranslations();
	const tooltip = t("pricing.allowanceTooltip", {
		videoCredits: CREDITS_PER_VIDEO,
		imageCredits: CREDITS_PER_IMAGE,
	});
	return (
		<span>
			{t("pricing.allowance", {
				videos: Math.floor(credits / CREDITS_PER_VIDEO),
				images: Math.floor(credits / CREDITS_PER_IMAGE),
			})}
			<TooltipProvider delayDuration={0}>
				<Tooltip>
					<TooltipTrigger asChild>
						<HelpCircleIcon
							className="ml-1 inline size-4 cursor-help align-text-bottom text-muted-foreground"
							aria-label={tooltip}
						/>
					</TooltipTrigger>
					<TooltipContent className="max-w-xs">
						<p className="text-sm leading-relaxed">{tooltip}</p>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</span>
	);
}

export function PricingTable({
	className,
	userId,
	organizationId,
	activePlanId,
	hideFree,
	source = "pricing-page",
	reason,
	defaultTab,
	variant = "default",
	compactMobile,
}: {
	className?: string;
	userId?: string;
	organizationId?: string;
	activePlanId?: string;
	hideFree?: boolean;
	/** 首屏停在哪个 tab。撞墙弹窗传 "onetime"，让「积分不够」的人第一眼
	 * 看到的是 $6.9 补分而不是订阅。没有可售的包时自动退回月付。 */
	defaultTab?: "month" | "year" | "onetime";
	/** Where this table is rendered — lets analytics split checkout clicks by
	 * entry point (e.g. "paywall-modal" vs the standalone pricing pages). */
	source?: string;
	/** 付费墙弹窗的触发原因（paywall-viewed 的 reason），原样带进 checkout-clicked，
	 * 才能按原因算 paywall-viewed → checkout-clicked。定价页不传。 */
	reason?: string;
	/** "deevid"：首页用的 DeeVid V2 卡片外观（DESIGN.md §7.3）。只换样式，
	 * 逻辑和 /pricing、付费墙完全一致；不传就是原样。 */
	variant?: "default" | "deevid";
	/** 单列（手机）时特性只留高亮条目可展开。deevid 默认开；/pricing、
	 * /subscription、撞墙弹窗都显式开着。价格位置不归它管：订阅卡一律在套餐名下 */
	compactMobile?: boolean;
}) {
	const dv = variant === "deevid";
	const compact = compactMobile ?? dv;
	const t = useTranslations();
	const router = useRouter();
	const localeCurrency = useLocaleCurrency();
	const [loading, setLoading] = useState<PlanId | false>(false);
	const [loadingPack, setLoadingPack] = useState<string | false>(false);
	// compact 卡在手机上（单列）默认只露带高亮的特性（分辨率、4K），一个开关管四张卡
	const [showAllFeatures, setShowAllFeatures] = useState(false);
	const CREDIT_PACKS_ENABLED = true;
	// 只展示已配置 productId 的 pack（env 未设置时不渲染）
	// credit pack 按 userId 发放（webhook 里没有 organization 场景的发放逻辑），
	// 组织上下文一律不展示，避免扣款成功但积分发不出去
	const availablePacks =
		CREDIT_PACKS_ENABLED && !organizationId
			? (creditPacks ?? []).filter(
					(pack) =>
						!pack.retired &&
						pack.productId &&
						pack.productId.trim() !== "",
				)
			: [];
	// 积分包「每积分单价 / 比最小包省多少」的基准：在售包里每积分最贵的那个
	const basePackUnitPrice = Math.max(
		...availablePacks.map((pack) => pack.price / pack.credits),
	);

	// 三选一：订阅月付 / 订阅年付 / 一次性积分包
	// 默认停在月付：年付总价在首屏直接把人吓走，不管是营销定价页还是
	// 已经有购买意向的订阅页/升级弹窗。调用方可以用 defaultTab 覆盖——
	// 传 "onetime" 时，如果包不可售，这里必须退回月付，否则首屏空白。
	const [tab, setTab] = useState<"month" | "year" | "onetime">(() =>
		defaultTab === "onetime" && availablePacks.length === 0
			? "month"
			: (defaultTab ?? "month"),
	);
	// 订阅卡片用的计费周期；onetime 视图不渲染订阅卡片，取 year 占位
	const interval: "month" | "year" = tab === "onetime" ? "year" : tab;
	const { activePlan } = usePurchases(organizationId);

	// 获取activeProductId
	const activeProductId = activePlan?.price?.productId;

	// 直接从activePlan获取interval
	const activeInterval = activePlan?.interval as "month" | "year" | null;

	const { planData } = usePlanData();

	const createCheckoutLinkMutation = useCreateCheckoutLinkMutation();
	const changeSubscriptionPlanMutation = useChangeSubscriptionPlanMutation();
	const queryClient = useQueryClient();
	const { trackEvent: trackUmamiEvent } = useUmamiAnalytics();
	const { trackEvent: trackClarityEvent, prioritizeRecording } =
		useClarityAnalytics();
	const { showLoginModal } = useLoginModal();
	// CTA 文案实验。桶在 proxy.ts 里按 cookie 分，这里只读。唯一的变量是按钮
	// 文字——其余改动两桶同时生效，所以差异能干净地归到文案头上。
	const ctaVariant = useAbVariant("ab_cta");
	// 微信支付只对简体中文界面开放：CNY 结账在 Waffo 上只出微信，对其他市场
	// 的用户既看不懂也用不了，露出来只会制造困惑。zh-TW / zh-HK 同样排除。
	const locale = useLocale();
	const showWechatPay = locale === "zh";
	// Locale-stripped, so /zh/ai-edit-image and /ai-edit-image land on one row.
	const pathname = useLocalePathname();

	// Fire the checkout event to both trackers and return a promise that settles
	// once they've had their window. The redirect would cancel Umami's beacon and
	// drop Clarity's batched upload, so it has to wait on this — but we start it
	// *before* the checkout-link request so the wait overlaps that round trip
	// instead of stacking on top of it. Waiting on both trackers matters: when
	// one is blocked it resolves instantly, and racing would cut the other short.
	// The timeout guard is shared, so a stalled tracker can't hold checkout up.
	const trackCheckout = (data: Record<string, unknown>) => {
		prioritizeRecording("checkout");

		return Promise.race([
			Promise.all([
				trackClarityEvent("checkout-clicked"),
				trackUmamiEvent("checkout-clicked", {
					source,
					page: normalizePage(pathname),
					variant: ctaVariant,
					...(reason ? { reason } : {}),
					...data,
				}),
			]).catch(() => {}),
			new Promise((resolve) => setTimeout(resolve, 600)),
		]);
	};

	// The failure branch of a checkout press: no link came back, so the user
	// just gets a toast and stays put. Without this the attempt is invisible —
	// Umami only has checkout-clicked, and Creem never records the session
	// either (the API call it would have come from is the one that failed).
	// The error message separates "our API errored" from "the request never
	// left the device". No redirect follows, so this can fire and forget.
	const trackCheckoutFailed = (
		data: Record<string, unknown>,
		error: unknown,
	) => {
		void trackUmamiEvent("checkout-failed", {
			source,
			page: normalizePage(pathname),
			variant: ctaVariant,
			...(reason ? { reason } : {}),
			error: error instanceof Error ? error.message : String(error),
			...data,
		});
	};

	// 换档走的是 ChangePlanButton，但在 Waffo 下它同样是一次跳走付款的 hosted
	// checkout，所以复用同一套事件属性：checkout-clicked 在 Umami 里仍是一张表，
	// 用 changePlan 区分「新订」和「老用户升降档」两条路径。
	const changePlanEventData = (
		planId: string,
		price: { productId: string; type: string },
	) => ({
		planId,
		productId: price.productId,
		interval,
		priceType: price.type,
		changePlan: true,
	});

	const onSelectPlan = async (
		planId: PlanId,
		productId?: string,
		isResume = false,
	) => {
		if (!(userId || organizationId)) {
			// 弹出登录模态框而不是重定向
			showLoginModal("/subscription");
			return;
		}

		// 对于免费版，如果没有productId，直接跳转到应用（免费版是自动分配的）
		const plan = plans[planId];
		if (plan.isFree && (!productId || productId.trim() === "")) {
			router.push("/app");
			return;
		}

		// Validate productId for non-free plans
		if (!productId || productId.trim() === "") {
			toast.error("Invalid plan selected");
			return;
		}

		// 已有生效订阅时走改套餐，而不是再开一次结账。
		// Waffo 返回正式换档确认页，
		// 用户付完款由 webhook 落库；拿不到链接说明是能直接改的通道，刷新即可。
		// 存量 Creem 订阅（sub_*）不走改套餐：Waffo 没法给 Creem 的订阅按比例
		// 结算。直接当作新订一个 Waffo 套餐，激活后 webhook 会自动把 Creem 那条
		// 期末取消，不会两边同时扣费。
		const isLegacyCreemPlan =
			activePlan?.subscriptionId?.startsWith("sub_");

		if (
			activePlan?.status === "active" &&
			activePlan.subscriptionId &&
			!isLegacyCreemPlan
		) {
			setLoading(planId);
			// 这条分支同样可能跳去 Waffo 付款，所以和普通结账一样先发事件。
			// 有生效订阅时价格一定是订阅价，priceType 直接给 recurring。
			const changePlanData = changePlanEventData(planId, {
				productId,
				type: "recurring",
			});
			try {
				const tracked = trackCheckout(changePlanData);

				const result = await changeSubscriptionPlanMutation.mutateAsync(
					{
						subscriptionId: activePlan.subscriptionId,
						newProductId: productId,
						redirectUrl: window.location.href,
					},
				);

				if (result.checkoutUrl) {
					await tracked;
					window.location.href = result.checkoutUrl;
					return;
				}

				await queryClient.invalidateQueries({
					queryKey: purchasesQueryKey(organizationId ?? undefined),
				});
			} catch (error) {
				console.error("Failed to change subscription plan:", error);
				trackCheckoutFailed(changePlanData, error);
				toast.error(
					error instanceof Error
						? error.message
						: t(
								"settings.billing.createCustomerPortal.notifications.error.title",
							),
				);
			} finally {
				setLoading(false);
			}
			return;
		}

		// 如果plan已经在上面获取过，这里不需要重新获取
		// const plan = plans[planId]; // 已在上面获取
		const price = plan.prices?.find(
			(price) => price.productId === productId,
		);

		if (!price) {
			return;
		}

		setLoading(planId);

		try {
			// Started here, not after the request: the event is the click, and
			// this way the tracker flush runs while we wait for the link.
			const tracked = trackCheckout({
				planId,
				productId,
				interval,
				priceType: price.type,
			});

			const { checkoutLink } =
				await createCheckoutLinkMutation.mutateAsync({
					type:
						price.type === "one-time" ? "one-time" : "subscription",
					productId,
					organizationId,
					redirectUrl: window.location.href,
				});

			// Usually already settled by now, so this adds nothing.
			await tracked;
			window.location.href = checkoutLink;
		} catch (error) {
			console.error(error);
			trackCheckoutFailed(
				{
					planId,
					productId,
					interval,
					priceType: price.type,
				},
				error,
			);
			toast.error("Failed to create checkout");
		} finally {
			setLoading(false);
		}
	};

	const onSelectPack = async (
		packId: string,
		productId: string,
		currency: "USD" | "CNY" = "USD",
	) => {
		if (!(userId || organizationId)) {
			showLoginModal("/subscription");
			return;
		}
		if (organizationId) {
			// Credit grants are keyed by userId; org-billed purchases have no
			// user_id in checkout metadata, so the webhook can't grant credits.
			toast.error(
				"Credit packs are only available for personal accounts",
			);
			return;
		}
		if (!productId || productId.trim() === "") {
			toast.error("Invalid pack selected");
			return;
		}

		setLoadingPack(currency === "CNY" ? `${packId}:wechat` : packId);
		try {
			const tracked = trackCheckout({
				planId: packId,
				productId,
				priceType: "one-time",
				currency,
			});

			const { checkoutLink } =
				await createCheckoutLinkMutation.mutateAsync({
					type: "one-time",
					productId,
					organizationId,
					redirectUrl: window.location.href,
					currency,
				});

			await tracked;
			window.location.href = checkoutLink;
		} catch (error) {
			console.error(error);
			trackCheckoutFailed(
				{
					planId: packId,
					productId,
					priceType: "one-time",
				},
				error,
			);
			toast.error("Failed to create checkout");
		} finally {
			setLoadingPack(false);
		}
	};

	const filteredPlans = Object.entries(plans).filter(([planId, plan]) => {
		// 不再过滤掉当前激活的计划
		// 如果当前有激活的计划，不显示相同计划
		// if (planId === activePlanId) return false;

		// 如果指定隐藏免费计划
		if (hideFree && planId === "free") return false;

		// 如果当前已有付费计划，不显示免费计划
		if (planId === "free" && activePlanId && activePlanId !== "free")
			return false;

		// 检查计划是否有有效的价格和productId
		if (!plan.isEnterprise) {
			const hasValidPrice = plan.prices?.some(
				(price) => price.productId && price.productId.trim() !== "",
			);
			// 免费计划或者有有效价格的计划才显示
			return plan.isFree || hasValidPrice;
		}

		return true;
	});

	const hasSubscriptions = filteredPlans.some(([_, plan]) =>
		plan.prices?.some((price) => price.type === "recurring"),
	);

	// 根据功能内容决定使用哪个图标
	const getFeatureIcon = (feature: string): LucideIcon => {
		// 处理各种语言的图像转文本
		if (
			feature.includes("Image-to-Text") ||
			feature.includes("图像转文本") ||
			feature.includes("图像到文本")
		) {
			return ImageIcon;
		}
		// 处理无限制的文本到提示
		if (
			(feature.includes("Unlimited") || feature.includes("无限制")) &&
			(feature.includes("Text-to-Prompt") ||
				feature.includes("文本到提示") ||
				feature.includes("文本到提示生成"))
		) {
			return KeyboardIcon;
		}
		// 处理任何无限制的功能
		if (feature.includes("Unlimited") || feature.includes("无限制")) {
			return InfinityIcon;
		}
		// 处理文本到提示功能
		if (
			feature.includes("Text-to-Prompt") ||
			feature.includes("文本到提示") ||
			feature.includes("文本到提示生成")
		) {
			return MessageSquareTextIcon;
		}
		// 处理额外积分
		if (feature.includes("Bonus") || feature.includes("额外")) {
			return GiftIcon;
		}
		// 处理商业许可
		if (
			feature.includes("Commercial License") ||
			feature.includes("商业许可")
		) {
			return BriefcaseIcon;
		}
		// 处理Flux相关功能
		if (feature.includes("Flux.1")) {
			return Zap;
		}

		// 默认图标
		return CheckIcon;
	};

	/**
	 * 卡片配色。强调文字（积分数、分辨率）不走这里——全站统一用 primary，见
	 * HIGHLIGHT_CLASS；这里只管按钮和徽章的层级。
	 *
	 * Starter 与 Pro 刻意同色：Pro 的「更值得买」已经由边框和 Recommended 徽章
	 * 表达，按钮再深一档会读成两个不同的动作。Ultimate 用深色而不是更亮的颜色，
	 * 旗舰档压得住才像旗舰；之前的 bg-secondary 在这套主题里渲染成粉色，和整张
	 * 表的蓝色系打架。
	 *
	 * 勾图标四档一律 primary：层级只由按钮表达，勾再分深浅就成了四套蓝。
	 */
	const getPlanStyles = (planId: string, recommended = false) => {
		// DeeVid 层级：靛蓝只给推荐档，其余付费档黑胶囊，免费档灰胶囊
		if (dv) {
			const pill = "rounded-full border-0 shadow-none";
			return {
				button:
					planId === "free"
						? `${pill} bg-gray-4 text-gray-12 hover:bg-gray-5`
						: recommended
							? `${pill} bg-brand text-white shadow-cta hover:bg-brand-500`
							: `${pill} bg-gray-12 text-white hover:bg-gray-11`,
				icon: "text-brand",
				badge: "bg-brand text-white",
			};
		}
		switch (planId) {
			case "free":
				return {
					button: "bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-200 shadow-none",
					icon: "text-primary",
					badge: "bg-blue-100 text-blue-700",
				};
			case "starter":
				return {
					button: "bg-primary text-white hover:bg-primary/90 border-primary",
					icon: "text-primary",
					badge: "bg-primary text-white",
				};
			case "pro":
				return {
					button: "bg-primary text-white hover:bg-primary/90 border-primary shadow-md",
					icon: "text-primary",
					badge: "bg-primary text-white",
				};
			case "ultimate":
				return {
					button: "bg-foreground text-background hover:bg-foreground/90 border-foreground shadow-md",
					icon: "text-primary",
					badge: "bg-foreground text-background",
				};
			default:
				return {
					button: recommended
						? "bg-primary text-white"
						: "bg-secondary text-white",
					icon: "text-primary",
					badge: "bg-primary text-white",
				};
		}
	};

	// 文案里 **…** 包住的片段（积分数、分辨率）加粗并染成同一个强调色。刻意**不**
	// 跟档位走：四档四种蓝读起来像四种含义，用户第一眼看不出「这些都是额度」。
	const renderHighlighted = (text: React.ReactNode) => {
		if (typeof text !== "string" || !text.includes("**")) {
			return text;
		}
		return text.split("**").map((part, index) =>
			index % 2 === 1 ? (
				<strong
					key={`${index}-${part}`}
					className={cn(
						"font-semibold",
						dv ? "text-brand" : "text-primary",
					)}
				>
					{part}
				</strong>
			) : (
				part
			),
		);
	};

	// 订阅卡和积分包共用的分区列表：小标题 + 一列带图标的条目。
	// __LOCKED__ 前缀的条目画成灰色划线（免费档里被锁的能力），不做高亮。
	const renderFeatureSection = (
		heading: string,
		items: React.ReactNode[],
		planId: string,
		recommended: boolean,
		/** 单列时只留 **…** 高亮的条目，其余到 @xl 再出 */
		collapsed = false,
	) => {
		if (!items.length) {
			return null;
		}
		return (
			<div className="mt-4">
				<p className="mb-2 font-semibold text-muted-foreground text-xs uppercase tracking-wide">
					{heading}
				</p>
				<ul className="grid list-none gap-2 text-sm">
					{items.map((feature, key) => {
						const locked =
							typeof feature === "string" &&
							feature.startsWith("__LOCKED__");
						const text = locked
							? (feature as string).slice("__LOCKED__".length)
							: feature;
						const FeatureIcon = locked
							? XIcon
							: typeof text === "string"
								? getFeatureIcon(text)
								: CheckIcon;
						return (
							<li
								key={key}
								className={cn(
									"flex items-center justify-start",
									collapsed &&
										(locked ||
											typeof text !== "string" ||
											!text.includes("**")) &&
										"hidden @xl:flex",
								)}
							>
								<FeatureIcon
									className={cn(
										"mr-3 size-5 flex-shrink-0",
										locked
											? "text-muted-foreground/40"
											: getPlanStyles(planId, recommended)
													.icon,
									)}
								/>
								<span
									className={cn(
										locked &&
											"text-muted-foreground/50 line-through",
									)}
								>
									{locked ? text : renderHighlighted(text)}
								</span>
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	// 积分包的购买按钮（价格 2026-10-05 起和订阅卡一样挪到标题下）。抽出来是因为
	// 那段 JSX 已经嵌了五层，内联会把卡片的结构读没。
	const renderPackCta = (pack: (typeof creditPacks)[number]) => (
		<div>
			<Button
				className={cn(
					"mt-4 w-full",
					dv
						? "rounded-full border-0 bg-gray-12 text-white hover:bg-gray-11"
						: "bg-blue-500 text-white hover:bg-blue-600 border-blue-600 cta-glow",
				)}
				onClick={() => onSelectPack(pack.id, pack.productId)}
				loading={loadingPack === pack.id}
			>
				{t(
					ctaVariant === "B"
						? "pricing.creditPacks.buyB"
						: "pricing.creditPacks.buy",
				)}
				<ArrowRightIcon className="ml-2 size-4" />
			</Button>
			{showWechatPay ? (
				<Button
					variant="outline"
					className={cn(
						"mt-2 w-full border-[#07C160] text-[#07C160] hover:bg-[#07C160] hover:text-white",
						dv && "rounded-full",
					)}
					onClick={() => onSelectPack(pack.id, pack.productId, "CNY")}
					loading={loadingPack === `${pack.id}:wechat`}
				>
					<WechatPayLogo className="mr-2 size-4" />
					{t("pricing.creditPacks.buyWithWechat", {
						price: pack.priceCNY,
					})}
				</Button>
			) : (
				<p className="mt-2 mb-0 flex items-center justify-center gap-1.5 text-muted-foreground text-xs">
					<WechatPayLogo className="size-3.5 text-[#07C160]" />
					{t("pricing.creditPacks.wechatAccepted")}
				</p>
			)}
		</div>
	);

	// 处理并更新功能特性，使用credits.ts中的数据
	const getUpdatedFeatures = (
		planId: string,
		features: React.ReactNode[],
	): React.ReactNode[] => {
		const updatedFeatures = [...features];
		const creditPlan = creditPlans[planId];

		if (!creditPlan) return updatedFeatures;

		// 处理IMAGE_TO_TEXT特性
		const imageToTextCredit = creditPlan.credits.find(
			(credit) => credit.type === "IMAGE_TO_TEXT",
		);
		if (imageToTextCredit) {
			const imageToTextIndex = updatedFeatures.findIndex(
				(feature) =>
					typeof feature === "string" &&
					(feature.includes("Image-to-Text") ||
						feature.includes("图像转文本") ||
						feature.includes("图像到文本")),
			);

			if (imageToTextIndex >= 0) {
				// 如果是无限制
				if (imageToTextCredit.amount === Number.POSITIVE_INFINITY) {
					updatedFeatures[imageToTextIndex] = t(
						"pricing.unlimitedImageToText",
					);
				} else {
					updatedFeatures[imageToTextIndex] = t(
						"pricing.imageToTextAmount",
						{
							amount: imageToTextCredit.amount,
							period: imageToTextCredit.period,
						},
					);
				}
			}
		}

		// 处理TEXT_TO_IMAGE特性 (对应Flux.1或Bonus)
		const textToImageCredit = creditPlan.credits.find(
			(credit) => credit.type === "IMAGE_GENERATION",
		);
		if (textToImageCredit) {
			// 额度行：数字全部来自 config/credits.ts，翻译文件只留占位符
			// __CREDITS__=总额度 / __ALLOWANCE__=「最多几条视频或几张图」那一行（带 tooltip）
			if (textToImageCredit.amount !== Number.POSITIVE_INFINITY) {
				const credits = textToImageCredit.amount;
				for (
					let index = 0;
					index < updatedFeatures.length;
					index += 1
				) {
					const feature = updatedFeatures[index];
					if (feature === "__ALLOWANCE__") {
						updatedFeatures[index] = (
							<AllowanceLine credits={credits} />
						);
					} else if (typeof feature === "string") {
						updatedFeatures[index] = feature.replace(
							"__CREDITS__",
							String(credits),
						);
					}
				}
			}

			// 付费计划处理Bonus credits - 寻找包含Bonus的特性行
			if (planId !== "free") {
				// 付费计划处理Bonus credits - 寻找包含Bonus的特性行
				const bonusIndex = updatedFeatures.findIndex(
					(feature) =>
						typeof feature === "string" &&
						(feature.includes("Bonus") || feature.includes("额外")),
				);

				if (bonusIndex >= 0) {
					// 始终使用TEXT_TO_IMAGE中的数量作为bonus值，不依赖翻译文件中的硬编码值
					const bonusAmount = textToImageCredit.amount;

					// 无论当前语言是什么，都使用来自config/credits.ts的实际值
					updatedFeatures[bonusIndex] = t("pricing.bonusCredits", {
						amount: bonusAmount,
					});
				}
			}
		}

		return updatedFeatures;
	};

	return (
		<div className={cn("@container", className)}>
			{activePlan?.status === "active" &&
				activePlan.price &&
				"interval" in activePlan.price &&
				activePlan.price.interval === "month" &&
				!activePlan.subscriptionId?.startsWith("sub_") && (
					<p className="mb-4 text-sm text-muted-foreground">
						{t("pricing.monthlyUpgradeRollover")}
					</p>
				)}
			{hasSubscriptions && (
				<div className="mb-6 flex @xl:justify-center">
					<Tabs
						value={tab}
						onValueChange={(value) => setTab(value as typeof tab)}
						data-test="price-table-interval-tabs"
					>
						<TabsList
							className={cn(
								"border-foreground/10",
								dv &&
									"gap-1 rounded-full border-0 bg-white p-1 shadow-card [&>button]:h-8 [&>button]:rounded-full [&>button]:border-0 [&>button]:px-5 sm:[&>button]:px-8 [&>button[data-state=active]]:bg-gray-12 [&>button[data-state=active]]:text-white",
							)}
						>
							<TabsTrigger value="month">
								{t("pricing.monthly")}
							</TabsTrigger>
							<TabsTrigger value="year" className="group">
								<span className="flex items-center gap-1.5">
									{t("pricing.yearly")}
									<span className="hidden @lg:inline-flex rounded-full bg-orange-700 px-2 py-0.5 text-[10px] font-extrabold text-white ring-1 ring-orange-500/30 shadow-sm shadow-orange-600/30">
										{(() => {
											let max = 0;
											for (const plan of Object.values(
												config.payments.plans,
											)) {
												if (
													"isFree" in plan ||
													"isEnterprise" in plan ||
													!("prices" in plan)
												)
													continue;
												const prices =
													plan.prices as any[];
												const monthly = prices?.find(
													(p) =>
														p.type ===
															"recurring" &&
														p.interval === "month",
												)?.amount;
												const yearly = prices?.find(
													(p) =>
														p.type ===
															"recurring" &&
														p.interval === "year",
												)?.amount;
												if (monthly && yearly) {
													const discount =
														(1 -
															yearly /
																(monthly *
																	12)) *
														100;
													if (discount > max)
														max = discount;
												}
											}
											return max > 0
												? t("pricing.yearlySaveBadge", {
														percent:
															Math.round(max),
													})
												: null;
										})()}
									</span>
								</span>
							</TabsTrigger>
							{availablePacks.length > 0 && (
								<TabsTrigger value="onetime">
									{t("pricing.creditPacks.tab")}
								</TabsTrigger>
							)}
						</TabsList>
					</Tabs>
				</div>
			)}
			{tab !== "onetime" && (
				<div
					className={cn("grid grid-cols-1 gap-6", {
						"@xl:grid-cols-2": filteredPlans.length >= 2,
						"@4xl:grid-cols-3": filteredPlans.length >= 3,
						"@5xl:grid-cols-4": filteredPlans.length >= 4,
					})}
				>
					{filteredPlans.map(([planId, plan]) => {
						const { isFree, isEnterprise, prices, recommended } =
							plan;
						const {
							title,
							credits: originalCredits,
							features: originalFeatures,
						} = planData[planId as keyof typeof planData];

						// 使用credits.ts数据更新特性
						const creditLines = getUpdatedFeatures(
							planId,
							originalCredits,
						);
						const features = getUpdatedFeatures(
							planId,
							originalFeatures,
						);

						// 从credits.ts获取价格
						const creditPrice = creditPlans[planId]?.price || 0;
						const creditCurrency =
							creditPlans[planId]?.currency || "USD";

						// 根据当前interval获取对应的产品ID
						const planProductId =
							prices?.find(
								(p) =>
									p.type === "recurring" &&
									p.interval === interval,
							)?.productId || "";

						// 首先使用credits.ts中的价格
						let price: any = null;

						if (isFree) {
							price = {
								amount: 0,
								currency: localeCurrency,
								interval,
								productId: planProductId,
								type: "recurring",
							};
						} else if (creditPrice > 0) {
							// 优先使用credits.ts中的价格，但保留原始productId
							// 优先使用config中的价格，如果没有则回退到creditPrice * 10
							const configYearlyAmount = prices?.find(
								(p) =>
									p.type === "recurring" &&
									p.interval === "year",
							)?.amount;
							const amount =
								interval === "year"
									? (configYearlyAmount ?? creditPrice * 10)
									: creditPrice;

							// 从config中获取当前interval对应的price对象，以保留trialPeriodDays等字段
							const configPriceForInterval = prices?.find(
								(p) =>
									p.type === "recurring" &&
									p.interval === interval,
							);

							price = {
								amount,
								currency: creditCurrency,
								interval,
								productId: planProductId, // 使用配置中的productId
								type: "recurring",
								...(configPriceForInterval &&
								"trialPeriodDays" in configPriceForInterval &&
								configPriceForInterval.trialPeriodDays
									? {
											trialPeriodDays:
												configPriceForInterval.trialPeriodDays,
										}
									: {}),
							};
						} else {
							// 如果credits.ts中没有价格，则使用config中的价格
							price = prices?.find(
								(price) =>
									!price.hidden &&
									(price.type === "one-time" ||
										price.interval === interval) &&
									price.currency === localeCurrency,
							);
						}

						if (!(price || isEnterprise)) {
							return null;
						}

						// 检查是否是当前激活的计划，需要同时比较planId和interval
						// 1. 首先确认planId是否匹配
						// 2. 如果是周期性计划（subscription），则必须检查interval是否匹配
						// 3. 只有当前计划ID匹配且interval匹配时才为true
						const isCurrentPlan =
							planId === activePlanId &&
							(isFree ||
								price?.type !== "recurring" ||
								activeInterval === interval);

						const priceBlock = price ? (
							<div>
								{interval === "year" ? (
									<>
										<div className="flex flex-wrap items-baseline gap-x-2">
											<span className="text-3xl lg:text-4xl font-bold">
												{Intl.NumberFormat("en-US", {
													style: "currency",
													currency: price.currency,
													minimumFractionDigits: 0,
												}).format(price.amount / 12)}
											</span>
											<span className="whitespace-nowrap text-sm font-normal opacity-60">
												{t("pricing.usdPerMonth")}
											</span>
											{(() => {
												const monthlyPrice =
													prices?.find(
														(p) =>
															p.type ===
																"recurring" &&
															p.interval ===
																"month",
													)?.amount ?? creditPrice;
												if (!monthlyPrice) return null;
												return (
													<span className="text-sm font-normal opacity-40 line-through ml-1">
														{Intl.NumberFormat(
															"en-US",
															{
																style: "currency",
																currency:
																	price.currency,
																minimumFractionDigits: 0,
															},
														).format(monthlyPrice)}
													</span>
												);
											})()}
										</div>
										<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
											{/* 实收金额。以前是 text-xs/opacity-50，用户只记住上面那个
											 * 「$5.92 / mo」的大字，到 Creem 看到年费全额就退出去了 */}
											<span className="whitespace-nowrap text-sm font-semibold">
												{Intl.NumberFormat("en-US", {
													style: "currency",
													currency: price.currency,
													minimumFractionDigits: 0,
												}).format(price.amount)}{" "}
												{t("pricing.usdBilledYearly")}
											</span>
											{!isFree &&
												(() => {
													const monthlyPrice =
														prices?.find(
															(p) =>
																p.type ===
																	"recurring" &&
																p.interval ===
																	"month",
														)?.amount ??
														creditPrice;
													if (!monthlyPrice)
														return null;
													const discount = Math.round(
														(1 -
															price.amount /
																(monthlyPrice *
																	12)) *
															100,
													);
													if (discount <= 0)
														return null;
													return (
														<span className="whitespace-nowrap rounded-full bg-green-500 px-2 py-0.5 text-[11px] font-extrabold text-white shadow-sm shadow-green-500/20">
															{t(
																"pricing.yearlySaveBadge",
																{
																	percent:
																		discount,
																},
															)}
														</span>
													);
												})()}
										</div>
									</>
								) : (
									<div className="flex items-baseline gap-2">
										<span className="text-3xl lg:text-4xl font-bold">
											{Intl.NumberFormat("en-US", {
												style: "currency",
												currency: price.currency,
												minimumFractionDigits: 0,
											}).format(price.amount)}
										</span>
										<span className="text-sm font-normal opacity-60">
											{t("pricing.usdPerMonth")}
										</span>
									</div>
								)}
							</div>
						) : null;

						return (
							<div
								key={planId}
								className={cn(
									dv
										? "rounded-20 bg-white p-7 shadow-card"
										: "rounded-xl border bg-card p-7",
									recommended &&
										(dv
											? "ring-2 ring-brand"
											: "border-2 border-primary"),
								)}
								data-test="price-table-plan"
							>
								<div className="flex h-full flex-col justify-between gap-4">
									<div>
										{recommended && (
											<div className="-mt-9 flex justify-center">
												<div
													className={cn(
														"mb-2 flex h-6 w-auto items-center gap-1.5 rounded-full px-2 py-1 font-semibold text-xs",
														getPlanStyles(
															planId,
															recommended,
														).badge,
													)}
												>
													<StarIcon className="size-3" />
													{t("pricing.recommended")}
												</div>
											</div>
										)}
										<h3
											className={cn(
												"my-0 font-semibold text-2xl",
												{
													"font-bold text-primary":
														recommended,
												},
											)}
										>
											{title}
										</h3>
										{/* 价格紧跟套餐名（2026-10-05 桌面也挪上来）：不用滑过整张功能表才看到 */}
										{priceBlock && (
											<div className="mt-3">{priceBlock}</div>
										)}

										{renderFeatureSection(
											t("pricing.sections.credits"),
											creditLines,
											planId,
											recommended ?? false,
										)}
										{renderFeatureSection(
											t("pricing.sections.features"),
											features,
											planId,
											recommended ?? false,
											compact && !showAllFeatures,
										)}
										{compact && features.length > 0 && (
											<button
												type="button"
												onClick={() =>
													setShowAllFeatures(
														(open) => !open,
													)
												}
												className="mt-2 inline-flex items-center gap-1 py-2 font-medium text-brand text-sm @xl:hidden"
											>
												{t(
													showAllFeatures
														? "pricing.showFewerFeatures"
														: "pricing.showAllFeatures",
												)}
												<ChevronDownIcon
													className={cn(
														"size-4 transition-transform",
														showAllFeatures &&
															"rotate-180",
													)}
												/>
											</button>
										)}

										{price &&
											"trialPeriodDays" in price &&
											price.trialPeriodDays && (
												<div className="mt-4 flex items-center justify-start font-medium text-primary text-sm opacity-80">
													<BadgePercentIcon className="mr-2 size-4" />
													{t("pricing.trialPeriod", {
														days: price.trialPeriodDays,
													})}
												</div>
											)}
									</div>

									<div>
										{isEnterprise ? (
											<Button
												className="mt-4 w-full"
												variant="outline"
												asChild
											>
												<LocaleLink href="/contact">
													<PhoneIcon className="mr-2 size-4" />
													{t("pricing.contactSales")}
												</LocaleLink>
											</Button>
										) : (
											<>
												{isCurrentPlan &&
												(activePlan?.status ===
													"active" ||
													activePlan?.status ===
														"trialing") ? (
													<Button
														className="mt-4 w-full"
														variant="outline"
														disabled={true}
													>
														{t(
															"pricing.currentPlan",
														)}
													</Button>
												) : (userId ||
														organizationId) &&
													activePlan?.status ===
														"active" &&
													activePlan?.id !== "free" &&
													price?.productId ? (
													<ChangePlanButton
														userId={userId}
														organizationId={
															organizationId
														}
														productId={
															price.productId
														}
														targetPlanPrice={
															price.amount
														}
														trackCheckout={() =>
															trackCheckout(
																changePlanEventData(
																	planId,
																	price,
																),
															)
														}
														trackCheckoutFailed={(
															error,
														) =>
															trackCheckoutFailed(
																changePlanEventData(
																	planId,
																	price,
																),
																error,
															)
														}
														className={cn(
															"mt-4 w-full",
															getPlanStyles(
																planId,
																recommended,
															).button,
														)}
													/>
												) : (
													<>
														<Button
															className={cn(
																"mt-4 w-full",
																getPlanStyles(
																	planId,
																	recommended,
																).button,
																// 免费档不亮（它不是一笔购买），
																// 其余付费档全部带动效。
																!isFree &&
																	!dv &&
																	"cta-glow",
															)}
															onClick={() =>
																onSelectPlan(
																	planId as PlanId,
																	price?.productId,
																	isCurrentPlan &&
																		activePlan?.status ===
																			"cancelled",
																)
															}
															loading={
																loading ===
																planId
															}
															disabled={
																// 如果用户未登录，所有按钮都可以点击（会弹出登录模态框）
																// 如果用户已登录：
																//   - 免费版：总是可以点击
																//   - 其他计划：需要productId
																!(
																	userId ||
																	organizationId
																)
																	? false
																	: isFree
																		? false
																		: !price?.productId
															}
															title=""
														>
															{interval ===
																"year" &&
															price?.trialPeriodDays ? (
																<>
																	{t(
																		"pricing.startFreeTrial",
																		{
																			days: price.trialPeriodDays,
																		},
																	)}
																	<ArrowRightIcon className="ml-2 size-4" />
																</>
															) : userId ||
																organizationId ? (
																t(
																	ctaVariant ===
																		"B"
																		? "pricing.choosePlanB"
																		: "pricing.choosePlan",
																)
															) : (
																<>
																	{t(
																		"pricing.getStarted",
																	)}
																	<ArrowRightIcon className="ml-2 size-4" />
																</>
															)}
														</Button>
														{interval === "year" &&
															price?.trialPeriodDays &&
															!isCurrentPlan && (
																<p className="mt-2 text-center text-xs text-muted-foreground">
																	{t(
																		"pricing.freeTrialCancelNote",
																		{
																			days: price.trialPeriodDays,
																		},
																	)}
																</p>
															)}
													</>
												)}
											</>
										)}
										{showWechatPay &&
											!isFree &&
											!isEnterprise &&
											price?.type === "recurring" && (
												<p className="mt-2 text-center text-xs text-muted-foreground">
													{t(
														"pricing.subscriptionPaymentHint",
													)}
												</p>
											)}
									</div>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{tab === "onetime" && availablePacks.length > 0 && (
				<div>
					<p className="mb-6 text-center text-sm text-muted-foreground">
						{t("pricing.creditPacks.subtitle")}
					</p>
					<div
						className={cn("grid grid-cols-1 gap-6", {
							"@xl:grid-cols-2": availablePacks.length >= 2,
							"@4xl:grid-cols-3": availablePacks.length === 3,
							"@5xl:grid-cols-4": availablePacks.length >= 4,
						})}
					>
						{availablePacks.map((pack) => (
							<div
								key={pack.id}
								className={cn(
									dv
										? "rounded-20 bg-white p-7 shadow-card"
										: "rounded-xl border bg-card p-7",
								)}
								data-test="credit-pack"
							>
								<div className="flex h-full flex-col justify-between gap-4">
									<div>
										<h4 className="my-0 font-semibold text-2xl">
											{t(
												"pricing.creditPacks.creditsLabel",
												{
													count: pack.credits,
												},
											)}
										</h4>
										<div className="mt-3 flex items-baseline gap-2">
											<span className="text-3xl lg:text-4xl font-bold">
												{Intl.NumberFormat("en-US", {
													style: "currency",
													currency: pack.currency,
												}).format(pack.price)}
											</span>
											<span className="text-sm font-normal opacity-60">
												USD
											</span>
										</div>
										{(() => {
											const unitPrice =
												pack.price / pack.credits;
											// 向下取整，宁可少说不夸大
											const savePercent = Math.floor(
												(1 - unitPrice / basePackUnitPrice) *
													100,
											);
											return (
												<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
													<span className="text-muted-foreground text-sm">
														{t(
															"pricing.creditPacks.perCredit",
															{
																price: Intl.NumberFormat(
																	"en-US",
																	{
																		style: "currency",
																		currency:
																			pack.currency,
																		maximumFractionDigits: 3,
																	},
																).format(unitPrice),
															},
														)}
													</span>
													{savePercent > 0 && (
														<span className="whitespace-nowrap rounded-full bg-green-500 px-2 py-0.5 text-[11px] font-extrabold text-white shadow-sm shadow-green-500/20">
															{t(
																"pricing.creditPacks.savePercent",
																{
																	percent:
																		savePercent,
																},
															)}
														</span>
													)}
												</div>
											);
										})()}
										{renderFeatureSection(
											t("pricing.sections.credits"),
											[
												<AllowanceLine
													key="allowance"
													credits={pack.credits}
												/>,
												t(
													"pricing.creditPacks.neverExpires",
												),
											],
											"pack",
											false,
										)}
										{renderFeatureSection(
											t("pricing.sections.features"),
											[
												t(
													"pricing.creditPacks.allTools",
												),
												...(pack.tier === "starter"
													? [
															t(
																"pricing.creditPacks.videoResolution",
															),
															t(
																"pricing.creditPacks.videoLocked",
															),
														]
													: [
															t(
																"pricing.creditPacks.videoHd",
															),
														]),
												t(
													"pricing.creditPacks.images4k",
												),
												t(
													"pricing.creditPacks.watermarkFree",
												),
											],
											"pack",
											false,
										)}
									</div>
									{renderPackCta(pack)}
								</div>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
